import {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import {
  getCurrentPageTableDataGrid,
  getPageSizeTableDataGrid,
  getRefreshTableDataGrid,
  getSearchValuesDataGrid,
} from 'src/domain/app/redux/data-grid/data-grid-selectors';
import AppPageMetadata from 'src/domain/core/AppPageMetadata';
import {useBreadcrumbContext} from 'src/domain/utility/AppContextProvider/BreadcrumbContextProvider';
import IntlMessages from 'src/domain/utility/IntlMessages';
import DataGrid from 'src/shared/components/data-grid';
import TableColumn from './columns';
import { toTreatmentRows } from "../treatment-row";
import {
  setDataTableDataGrid,
  setIsLoadingTableDataGrid,
  setSearchValuesDataGrid,
  setTotalTableDataGrid,
} from 'src/domain/app/redux/data-grid/data-grid-slice';
import hasPermission from 'src/shared/functions/access-control';
import {ManageTreatments} from 'src/shared/constants/permissions-enum';
import Error403 from '../../errorPages/Error403';
import {patientService} from 'src/shared/services/patient';
import {getNewNotification} from 'src/domain/app/redux/notifications/notification-selectors';
import {NotificationTypeValue} from 'src/shared/constants/AppEnums';
import {setTreatmentDetails} from 'src/domain/app/redux/treatments/treatments-slice';
import {Col, Row} from 'antd';
import {Input} from 'src/shared/components/input';
import {AiOutlineSearch} from 'react-icons/ai';
import {getMyProfile} from 'src/domain/app/redux/auth/auth-selectors';
import {UserType} from 'src/shared/constants/AppConst';

interface IArchivedTreatmentsProps {}

const ArchivedTreatments: React.FC<IArchivedTreatmentsProps> = () => {
  const {setBreadcrumb}: any = useBreadcrumbContext();
  const {messages} = useIntl();
  const dispatch = useDispatch();
  const currentPage = useSelector(getCurrentPageTableDataGrid);
  const pageSize = useSelector(getPageSizeTableDataGrid);
  const refreshTable = useSelector(getRefreshTableDataGrid);
  const searchTreatments = useSelector(getSearchValuesDataGrid);
  const newNotification = useSelector(getNewNotification);
  const myProfile = useSelector(getMyProfile);
  const canSearchByName = myProfile?.userType === UserType.Admin || myProfile?.userType === UserType.Coordinator;
  const [isTreatmentsList, setIsTreatmentsList] = useState(false);

  useEffect(() => {
    setBreadcrumb([
      {
        text: <IntlMessages id='treatment.actionNeeded' />,
        url: '/action-needed-treatments',
      },
    ]);
  }, [setBreadcrumb]);

  useEffect(() => {
    dispatch(setTreatmentDetails(undefined));
    setIsTreatmentsList(true);
  }, [dispatch]);

  useEffect(() => {
    if (
      (hasPermission(ManageTreatments.ArchivedTreatments) &&
        isTreatmentsList) ||
      newNotification?.Type === NotificationTypeValue.TreatmentOrder
    )
      (async () => {
        try {
          dispatch(setIsLoadingTableDataGrid(true));
          const archivedFilters: string[] = [];
          if (searchTreatments?.treatmentType) archivedFilters.push(`treatmentType==${searchTreatments.treatmentType}`);
          if (searchTreatments?.treatmentStatus) archivedFilters.push(`treatmentStatus==${searchTreatments.treatmentStatus}`);
          if (searchTreatments?.patientName) archivedFilters.push(`patientName@=*${searchTreatments.patientName}`);
          if (searchTreatments?.referenceNumber) archivedFilters.push(`patientReferenceNumber@=*${searchTreatments.referenceNumber}`);
          await patientService
            .getArchivedTreatments({
              Page: currentPage,
              PageSize: pageSize,
              Sorts: '-dateCreated',
              Filters: archivedFilters.length > 0 ? archivedFilters.join(',') : undefined,
            })
            .then((response) => {
              const data =
                response?.Data?.data?.length > 0 ? response?.Data?.data : [];
              dispatch(setDataTableDataGrid(toTreatmentRows(data)));
              dispatch(setTotalTableDataGrid(response?.Data?.totalItems));
            });
        } catch (error) {
          console.error(error);
        } finally {
          dispatch(setIsLoadingTableDataGrid(false));
        }
      })();
  }, [
    dispatch,
    isTreatmentsList,
    currentPage,
    pageSize,
    refreshTable,
    searchTreatments,
    newNotification,
  ]);

  return (
    <>
      {hasPermission(ManageTreatments.ArchivedTreatments) ? (
        <AppPageMetadata title={messages['treatment.archived'].toString()}>
          <DataGrid
            title='treatment.archived'
            showSearchFields
            titleTable='treatment.tableTitle'
            tableColumn={TableColumn()}
            optionChildren={
              <Row gutter={[20, 10]}>
                {canSearchByName && (
                  <Col span={12}>
                    <Input
                      allowClear
                      background='white'
                      placeholder={messages['common.searchForPatientName'].toString()}
                      prefix={<AiOutlineSearch />}
                      onChange={(e) => {
                        const value = e.target.value;
                        setTimeout(() => {
                          dispatch(setSearchValuesDataGrid({...searchTreatments, patientName: value || undefined}));
                        }, 1200);
                      }}
                    />
                  </Col>
                )}
                <Col span={canSearchByName ? 12 : 24}>
                  <Input
                    allowClear
                    background='white'
                    placeholder={messages['common.searchForReferenceNumber'].toString()}
                    prefix={<AiOutlineSearch />}
                    onChange={(e) => {
                      const value = e.target.value;
                      setTimeout(() => {
                        dispatch(setSearchValuesDataGrid({...searchTreatments, referenceNumber: value || undefined}));
                      }, 1200);
                    }}
                  />
                </Col>
              </Row>
            }
          />
        </AppPageMetadata>
      ) : (
        <Error403 />
      )}
    </>
  );
};

export default ArchivedTreatments;
